import { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import Api from './ajax';
import storage from './utils/storage';

import PageLoader from './components/common/PageLoader';
import ClasspepLoader from './components/common/ClasspepLoader';
import WelcomeScreenDialog from './components/welcome-screen/WelcomeScreenDialog';
import { setCurrentUser } from './features/currentUser';
import { setCheckAccess } from './features/checkAccess';
import { usePushNotifications } from './hooks/usePushNotifications';

const Login = lazy(() => import('./components/Login'));
const AppRoutes = lazy(() => import('./components/AppRoutes'));
const LoginIssue = lazy(() => import('./components/LoginIssue'));
const SMSNotifyPostProcess = lazy(() =>
  import('./components/sms-notify-post/Process'),
);
const OverAllView = lazy(() =>
  import('./components/sms-notify-post/OverAllView'),
);
const FeeCollectionForm = lazy(() =>
  import('./components/fee/FeeCollectionForm'),
);
const FeeOnlinePGWResponse = lazy(() =>
  import('./components/fee/FeeOnlinePGWResponse'),
);
const FeeCollectionStatusForm = lazy(() =>
  import('./components/fee/FeeCollectionStatusForm'),
);
const FeeCollectionStatusCheckForm = lazy(() =>
  import('./components/fee/FeeCollectionStatusCheckForm'),
);
const NoMenuRoutes = lazy(() => import('./components/NoMenuRoutes'));
const SimpleHomework = lazy(() =>
  import('./components/sms-notify-activity/SimpleHomework'),
);

const api = new Api();

const MainApp = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(null);
  const [fcmToken, setFcmToken] = useState(null);
  const [mobileDeviceInfo, setMobileDeviceInfo] = useState(null);
  const [showWelcomeScreen, setShowWelcomeScreen] = useState(false);
  const [welcomeCompleted, setWelcomeCompleted] = useState(false);
  const [appReady, setAppReady] = useState(false);

  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.currentUser.value);

  usePushNotifications();

  useEffect(() => {
    let isMounted = true;
    const loadAndAuth = async () => {
      // Use async storage for Capacitor/iOS
      const [instId, scholarId] = await Promise.all([
        storage.getAsync('inst_id'),
        storage.getAsync('scholar_id'),
      ]);

      if (!isMounted) return;

      api.post(
        '/has-auth',
        {
          inst_id: instId,
          scholar_id: scholarId,
          roll_type: process.env.REACT_APP_ROLE 
        },
        async (res) => {
          setIsLoggedIn(res.s);
          if (res.s) {
            api.post(
              '/fee-balance',
              {
                scholar_id: res.d.current_user.scholar_id,
              },
              (res2) => {
                if (res2.s) {
                  storage.setMultiple({
                    BlockAccess: res2.d.BlockAccess,
                    AppAccess: res2.d.AppAccess,
                  });
                  dispatch(setCheckAccess(res2.d));
                }
              },
            );
            dispatch(setCurrentUser(res.d.current_user));

            storage.setMultiple({
              inst_id: res.d.current_user.inst_id,
              scholar_id: res.d.current_user.scholar_id,
            });

            // Reset welcome screen states when user logs in
            setShowWelcomeScreen(true);
            setWelcomeCompleted(false);
            setAppReady(false);
          } else if (res.f === 1006 || res.f === '1006') {
            storage.clear();

            let registrations =
              await navigator.serviceWorker.getRegistrations();

            for (let ii = 0; ii < registrations.length; ii++)
              registrations[ii].unregister();
            window.location.reload(true);
          } else {
            let registrations =
              await navigator.serviceWorker.getRegistrations();

            for (let ii = 0; ii < registrations.length; ii++)
              registrations[ii].unregister();
          }
        },
      );
    };

    loadAndAuth();

    return () => {
      isMounted = false;
    };
  }, [dispatch]);

  useEffect(() => {
    // Only handle location changes after app is ready (welcome completed)
    if (appReady && welcomeCompleted) {
      const blockAccess = storage.get('BlockAccess');

      if (blockAccess === 'Y') {
        storage.set('location.pathname', '/fee-overdue-landing');
        onLocationChange('/fee-overdue-landing');
      } else {
        // Default to communication screen after welcome
        const targetPath =
          window.location.pathname === '/' ||
          window.location.pathname === '/sign-in'
            ? '/communication'
            : window.location.pathname;

        storage.set('location.pathname', targetPath);
        onLocationChange(targetPath);
      }
    }
  }, [appReady, welcomeCompleted]);

  function onLocationChange(loc) {
    if (!(loc === '/sign-in' || loc === '/signin-issue')) {
      storage.set('location.pathname', loc);
    }
  }

  useEffect(() => {
    window.addEventListener('native-fcm-token', (e) => {
      const { token, deviceInfo } = e.detail;

      if (token) {
        setFcmToken(token);
      }

      if (deviceInfo) {
        setMobileDeviceInfo(deviceInfo);
      }
    });

    return () => window.removeEventListener('native-fcm-token', () => {});
  }, []);

  useEffect(() => {
    if (isLoggedIn && fcmToken && mobileDeviceInfo) {
      const saveToken = () => {
        api.post('/fcm-register-v2', {
          token: fcmToken,
          deviceInfo: mobileDeviceInfo,
        });
      };

      saveToken();
    }
  }, [isLoggedIn, fcmToken, mobileDeviceInfo]);

  const handleWelcomeComplete = () => {
    setShowWelcomeScreen(false);
    setWelcomeCompleted(true);
    setAppReady(true);

    setTimeout(() => {
      onLocationChange('/communication');
    }, 100);
  };

  function isDisableMenu() {
    const cPath = window.location.pathname;

    if (cPath.indexOf('/fee-dismenu/online-pgw') !== -1) return true;

    return false;
  }

  let routeComponents;

  if (isLoggedIn === null) {
    routeComponents = <ClasspepLoader />;
  } else {
    if (!isLoggedIn) {
      routeComponents = (
        <BrowserRouter>
          <Routes>
            <Route
              path="sign-in"
              element={
                <Suspense fallback={<ClasspepLoader />}>
                  <Login
                    authenticate={() => {
                      setIsLoggedIn(true);
                      setShowWelcomeScreen(true);
                      setWelcomeCompleted(false);
                      setAppReady(false);
                    }}
                  />
                </Suspense>
              }
            />
            <Route
              path="signin-issue"
              element={
                <Suspense fallback={<ClasspepLoader />}>
                  <LoginIssue />
                </Suspense>
              }
            />
            <Route
              path="s"
              element={
                <Suspense fallback={<ClasspepLoader />}>
                  <SMSNotifyPostProcess />
                </Suspense>
              }
            />
            <Route
              path="sms-notify-post/:hash"
              element={
                <Suspense fallback={<ClasspepLoader />}>
                  <OverAllView />
                </Suspense>
              }
            />

            <Route path="p/fee">
              <Route
                path="coll/:hash"
                element={
                  <Suspense fallback={<PageLoader />}>
                    <FeeCollectionForm mode="I" />
                  </Suspense>
                }
              />
              <Route path="online-pgw">
                <Route
                  path="response"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <FeeOnlinePGWResponse mode="I" />
                    </Suspense>
                  }
                ></Route>
                <Route
                  path="status/:hash/:hash1"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <FeeCollectionStatusForm mode="P" />
                    </Suspense>
                  }
                ></Route>
                <Route
                  path="status-check/:hash/:hash1"
                  element={
                    <Suspense fallback={<PageLoader />}>
                      <FeeCollectionStatusCheckForm mode="P" />
                    </Suspense>
                  }
                ></Route>
              </Route>
            </Route>
            <Route
              path="sms-notify-activity/:hash"
              element={
                <Suspense fallback={<ClasspepLoader />}>
                  <SimpleHomework />
                </Suspense>
              }
            />

            <Route path="*" element={<Navigate to="/sign-in" />}></Route>
          </Routes>
        </BrowserRouter>
      );
    } else {
      if (showWelcomeScreen && !welcomeCompleted) {
        routeComponents = (
          <BrowserRouter>
            <Suspense fallback={<ClasspepLoader />}>
              <WelcomeScreenDialog
                isLoggedIn={isLoggedIn}
                currentUser={currentUser}
                onWelcomeComplete={handleWelcomeComplete}
              />
            </Suspense>
          </BrowserRouter>
        );
      } else if (!appReady || !welcomeCompleted) {
        routeComponents = <ClasspepLoader />; // Show loader until app is ready
      } else if (isDisableMenu()) {
        routeComponents = (
          <Suspense fallback={<ClasspepLoader />}>
            <NoMenuRoutes onLocationChange={onLocationChange} />
          </Suspense>
        );
      } else {
        routeComponents = (
          <Suspense fallback={<ClasspepLoader />}>
            <AppRoutes onLocationChange={onLocationChange} />
          </Suspense>
        );
      }
    }
  }

  return <>{routeComponents}</>;
};

export default MainApp;
